import { useMemo } from 'react';
import { useQuery } from 'react-query';

import { Form, Select, SelectProps } from 'antd';
import SelectSuffixIcon from '../../../components/SelectSuffixIcon/SelectSuffixIcon';

import { ClientService } from '../../../shared/api/ClientService';
import API from '../../../utils/api';
import useLocale from '../../../hooks/useLocale';

import { getAvailableTimeslotsByMeetingType, preserveSelection } from '../utils';
import { MEETING_TYPES_QUERY } from '../../../constants/reactQuery';
import { ISelectedTimeslot } from '../types';
import { ALL_OPTION_ID } from '../constants';

import '../SchedulingComponent.scss';

interface IProps extends SelectProps {
  initialTimeslots?: ClientService.AppointmentTimeSlotDto[];
  filterOutUnknown?: boolean;
}

const MeetinTypeSelect = ({ initialTimeslots, filterOutUnknown, ...props }: IProps) => {
  const { t } = useLocale();
  const { Option } = Select;

  const { data: meetingTypes } = useQuery([MEETING_TYPES_QUERY], () => API.listMeetingTypes());

  const typesWithAllOption = useMemo(
    () => [
      { name: t.ALL, id: ALL_OPTION_ID },
      ...((filterOutUnknown
        ? meetingTypes?.filter((i) => i.enumValue !== ClientService.MeetingTypeEnum.Unknown)
        : meetingTypes) || []),
    ],
    [meetingTypes, t.ALL, filterOutUnknown]
  );

  return (
    <Form.Item noStyle shouldUpdate>
      {({ setFieldsValue, getFieldValue }) => {
        return (
          <Form.Item label={t.APPOINTMENT_SCHEDULING_MEETING_TYPE} name="meetingTypeId">
            <Select
              {...props}
              suffixIcon={<SelectSuffixIcon />}
              size="large"
              onChange={(meetingTypeId) => {
                const prevSelectedTimeslot = getFieldValue('selectedTimeslot') as ISelectedTimeslot;
                const prevAgentId = getFieldValue('agentId') as string | undefined;

                const { availableTimeslots, selectedTimeslot, agentId } = preserveSelection(
                  initialTimeslots,
                  meetingTypeId,
                  prevSelectedTimeslot,
                  prevAgentId
                );

                setFieldsValue({
                  availableTimeslots,
                  selectedTimeslot,
                  agentId,
                });
              }}
            >
              {typesWithAllOption?.map((item) => (
                <Option key={`meetingType-${item.id}`} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        );
      }}
    </Form.Item>
  );
};

export default MeetinTypeSelect;
